import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Pagination,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  RefreshOutlined,
  EditOutlined,
  DeleteOutlineRounded,
  SearchRounded,
  VisibilityOutlined,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { adminService } from "../services/adminService";
import KnowledgeEditDialog from "../components/KnowledgeEditDialog";
import KnowledgeDescDialog from "../components/KnowledgeDescDialog";
import { textPrimary, textSecondary } from "../../../constants/consts";

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PAGE_SIZE = 13;

const normalize = (s = "") => String(s).toLocaleLowerCase("tr-TR").trim();

export default function AdminKnowledge() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);

  const [editDialog, setEditDialog] = useState({ open: false, item: null });
  const [descDialog, setDescDialog] = useState({
    open: false,
    title: "",
    desc: "",
  });

  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "info",
  });

  /* ─── Load ─── */
  const loadData = async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const data = await adminService.getKnowledge();
      const mapped = data.map((k) => ({
        id: String(k.id),
        title: k.title ?? "-",
        description: k.description ?? "",
        image: k.image ?? "",
      }));
      setRows(mapped);
      setPage(1);
    } catch (e) {
      console.error(e);
      setError("Bilgi bankası yüklenemedi.");
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, []);

  /* ─── Arama ─── */
  const filtered = useMemo(() => {
    const q = normalize(query);
    if (!q) return rows;
    return rows.filter(
      (r) =>
        normalize(r.title).includes(q) || normalize(r.description).includes(q),
    );
  }, [rows, query]);

  /* ─── Sayfalama ─── */
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  /* ─── Silme ─── */
  const handleDelete = async (row) => {
    if (!window.confirm(`"${row.title}" silinsin mi?`)) return;
    try {
      await adminService.deleteKnowledge(row.id);
      setSnack({ open: true, msg: "Kayıt silindi.", severity: "success" });
      await loadData(true);
    } catch (e) {
      console.error(e);
      setSnack({ open: true, msg: "Silme başarısız.", severity: "error" });
    }
  };

  /* ─── Render ─── */
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        gap: 1.5,
      }}
    >
      {/* Başlık */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          flexWrap: "wrap",
          animation: `${fadeInUp} 0.4s ease-out both`,
        }}
      >
        <Typography
          variant="h6"
          fontWeight={800}
          color="#fff"
          sx={{ fontSize: 18 }}
        >
          Bilgi Bankası
        </Typography>
        <Box
          sx={{
            px: 1.25,
            py: 0.4,
            borderRadius: 5,
            backgroundColor: "rgba(255,255,255,0.14)",
            color: "#fff",
            fontWeight: 700,
            fontSize: 12,
            border: "1px solid rgba(255,255,255,0.18)",
          }}
        >
          {filtered.length} kayıt
        </Box>

        <Tooltip title="Yenile">
          <span>
            <IconButton
              size="small"
              onClick={() => loadData(true)}
              disabled={loading || refreshing}
              sx={{
                ml: "auto",
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.18)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.22)" },
              }}
            >
              <RefreshOutlined
                sx={{
                  fontSize: 18,
                  animation:
                    loading || refreshing
                      ? "spin 1.2s linear infinite"
                      : "none",
                  "@keyframes spin": { to: { transform: "rotate(360deg)" } },
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Arama */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: 2.5,
          backgroundColor: "#fff",
          boxShadow: "0 8px 22px rgba(0,0,0,0.18)",
          flexShrink: 0,
          animation: `${fadeInUp} 0.45s ease-out 0.05s backwards`,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Konu veya içerik ara..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded sx={{ fontSize: 18, color: "#94A3B8" }} />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#fff" }} />
        </Box>
      ) : (
        <>
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              borderRadius: 2.5,
              display: "flex",
              flexDirection: "column",
              backgroundColor: "rgba(255,255,255,0.98)",
              boxShadow: "0 12px 32px rgba(0,0,0,0.20)",
              border: "1px solid rgba(255,255,255,0.30)",
              animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.1s backwards`,
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                "&::-webkit-scrollbar": { width: 6, height: 6 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(15,50,65,0.20)",
                  borderRadius: 3,
                },
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        background:
                          "linear-gradient(180deg, #F1F5F9 0%, #E2E8F0 100%)",
                        color: "#334155",
                        fontWeight: 800,
                        fontSize: 10.5,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        py: 1,
                        px: 1.5,
                        borderBottom: "1px solid #CBD5E1",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <TableCell>Foto</TableCell>
                    <TableCell>Konu</TableCell>
                    <TableCell>Bilgi</TableCell>
                    <TableCell align="right">İşlem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        sx={{
                          textAlign: "center",
                          py: 6,
                          color: textSecondary,
                          fontSize: 13,
                        }}
                      >
                        Kayıt bulunamadı
                      </TableCell>
                    </TableRow>
                  ) : (
                    pageRows.map((r, i) => (
                      <TableRow
                        key={r.id}
                        sx={{
                          backgroundColor: i % 2 ? "#F8FAFC" : "#fff",
                          "&:hover": {
                            backgroundColor: "rgba(4,87,129,0.06)",
                          },
                          transition: "background-color 0.2s ease",
                          "& td": {
                            py: 0.75,
                            px: 1.5,
                            fontSize: 12.5,
                            color: "#334155",
                            borderBottom: "1px solid #F1F5F9",
                          },
                        }}
                      >
                        <TableCell>
                          {r.image ? (
                            <Box
                              component="img"
                              src={r.image}
                              alt={r.title}
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 1.5,
                                objectFit: "cover",
                                border: "1px solid rgba(15,50,65,0.10)",
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 1.5,
                                backgroundColor: "#F1F5F9",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#94A3B8",
                                fontSize: 10,
                                border: "1px solid rgba(15,50,65,0.08)",
                              }}
                            >
                              —
                            </Box>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            color: textPrimary,
                            maxWidth: 240,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={r.title}
                        >
                          {r.title}
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 480,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={r.description}
                        >
                          {r.description}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Tooltip title="Görüntüle">
                            <IconButton
                              size="small"
                              onClick={() =>
                                setDescDialog({
                                  open: true,
                                  title: r.title,
                                  desc: r.description,
                                })
                              }
                              sx={{
                                color: "#15803D",
                                "&:hover": {
                                  backgroundColor: "rgba(21,128,61,0.10)",
                                },
                              }}
                            >
                              <VisibilityOutlined sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Düzenle">
                            <IconButton
                              size="small"
                              onClick={() =>
                                setEditDialog({ open: true, item: r })
                              }
                              sx={{
                                color: "#045781",
                                "&:hover": {
                                  backgroundColor: "rgba(4,87,129,0.10)",
                                },
                              }}
                            >
                              <EditOutlined sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Sil">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(r)}
                              sx={{
                                color: "#B91C1C",
                                "&:hover": {
                                  backgroundColor: "rgba(185,28,28,0.10)",
                                },
                              }}
                            >
                              <DeleteOutlineRounded sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </Paper>

          {filtered.length > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
                px: 0.5,
                animation: `${fadeInUp} 0.4s ease-out 0.2s backwards`,
              }}
            >
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.75)",
                  fontSize: 11.5,
                  fontWeight: 600,
                }}
              >
                {Math.min((safePage - 1) * PAGE_SIZE + 1, filtered.length)}–
                {Math.min(safePage * PAGE_SIZE, filtered.length)} /{" "}
                {filtered.length} kayıt
              </Typography>
              <Pagination
                count={totalPages}
                page={safePage}
                onChange={(_, p) => setPage(p)}
                size="small"
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "#fff",
                    "&.Mui-selected": {
                      backgroundColor: "rgba(255,255,255,0.24)",
                      fontWeight: 800,
                    },
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.14)" },
                  },
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* Dialoglar */}
      <KnowledgeDescDialog
        open={descDialog.open}
        onClose={() => setDescDialog({ open: false, title: "", desc: "" })}
        title={descDialog.title}
        desc={descDialog.desc}
      />

      <KnowledgeEditDialog
        open={editDialog.open}
        item={editDialog.item}
        onClose={() => setEditDialog({ open: false, item: null })}
        onSaved={() => {
          setSnack({
            open: true,
            msg: "Kayıt güncellendi.",
            severity: "success",
          });
          loadData(true);
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ borderRadius: 2 }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
